/* ============================================================================
   PANTALLA DEL SGS

   Reemplaza a la anterior. Monta el canal de seguimiento sobre los módulos ya
   probados: `domain/sgs/registroSgs.js`, `domain/sgs/matrizAndes.js` y
   `services/db/escrituraConfirmada.js`.

   ══════════════════════════════════════════════════════════════════════════
   LO QUE ESTA PANTALLA TIENE QUE HACER BIEN

   · **Nada aparece como guardado hasta que se confirmó la escritura.** Es el
     hallazgo crítico del análisis: antes, un reporte que no se guardaba seguía
     en la lista y se perdía al cerrar la aplicación.

   · **El estado del almacenamiento se comprueba al abrir**, no cuando el
     piloto ya escribió un reporte largo.

   · **Un reporte confidencial no muestra la identidad en ningún lado.** El
     manual de ANDES lo dice: «la confidencialidad se garantiza cuando el
     reporte es recibido por personal del SIG exclusivamente».

   · **El canal se ve.** Un reporte tiene que mostrar en qué paso está y a
     cuáles puede pasar — no un menú con todos los estados, que permitiría
     saltear la verificación de eficacia.
   ══════════════════════════════════════════════════════════════════════════
   ========================================================================== */

import React, { useCallback, useEffect, useMemo, useState } from "react";

import {
  ESTADOS, TIPOS, crearRegistro, cambiarEstado, anotar,
  vistaAnonima, verificarConjunto, exportar, indicadores,
  registrarResidual, saludDelVolumen, registrarBarreras,
} from "../../domain/sgs/registroSgs.js";
import { evaluar, PROBABILIDAD, SEVERIDAD, PROCEDENCIA } from "../../domain/sgs/matrizAndes.js";
import { resumenPreparacion, listaDePreparacion } from "../../domain/sgs/preparacionBars.js";
import {
  DIMENSIONES, ROLES_REPORTANTE, ROLES_GESTOR, conflictoDeInteres,
  ESTADO_BARRERA, BARRERAS_TIPICAS, orientacionDeBarreras,
} from "../../domain/sgs/sistemaIntegrado.js";
import { DEPOSITOS, migrar, hayPendientes } from "../../domain/sgs/migracion.js";
import {
  FORMATO, AVISO_NO_PUNITIVO, TIPOS_REPORTE, SISTEMAS, GUIA_DESCRIPCION, VIAS,
  armarReporte, siguienteNumero, huecosDeNumeracion, numeroCorrelativo,
} from "../../domain/sgs/formatoAndes.js";
import {
  agregarConfirmado, reemplazarConfirmado, comprobarAlmacenamiento, guardarConfirmado,
} from "../../services/db/escrituraConfirmada.js";
import { leer, guardar, borrar } from "../../services/db/almacen.js";
import "./sgs.css";

const CLAVE = "estado:sgsRegistros";

const ETIQUETA_TIPO = {
  reporte: "Reporte de seguridad",
  evento: "Evento / investigación",
  auditoria: "Auditoría",
  "no-conformidad": "No conformidad",
};

export default function PanelSgs({ quien: quienSugerido = "" }) {
  /* ═══════════════════════════════════════════════════════════════════════
     QUIÉN ACTÚA EN EL SGS

     Antes esto venía del piloto asignado al vuelo. Si no había vuelo cargado,
     `quien` quedaba vacío — y como el dominio EXIGE saber quién hace cada
     cambio, **ningún registro podía avanzar**. El SGS quedaba congelado sin
     decir por qué.

     Y el error de fondo era conceptual: quien gestiona el SGS es el personal
     del SIG, no el piloto que vuela hoy. Son roles distintos.

     Ahora es un campo propio que se recuerda entre sesiones, con el piloto
     asignado como sugerencia inicial. ═══════════════════════════════════ */
  /* El ROL de quien gestiona. El SMS Manager asesora a Gerencia General, no a
     la línea operacional: eso significa que un reporte sobre la operación no
     puede cerrarlo quien la conduce. La aplicación no tiene control de acceso
     —no puede impedirlo— pero registra el rol y advierte el conflicto, que es
     lo que un auditor busca. */
  const [rolGestor, setRolGestor] = useState(() => {
    try { return localStorage.getItem("hcSgsRol") || "sms-manager"; } catch { return "sms-manager"; }
  });

  useEffect(() => {
    try { localStorage.setItem("hcSgsRol", rolGestor); } catch { /* modo privado */ }
  }, [rolGestor]);

  const [quien, setQuien] = useState(() => {
    try { return localStorage.getItem("hcSgsQuien") || quienSugerido; } catch { return quienSugerido; }
  });

  useEffect(() => {
    if (!quien) return;
    try { localStorage.setItem("hcSgsQuien", quien); } catch { /* modo privado */ }
  }, [quien]);

  const [registros, setRegistros] = useState([]);
  const [cargando, setCargando] = useState(true);

  /* El error de escritura NO se puede cerrar: mientras no se guarde, el piloto
     tiene que saber que lo que ve no está a salvo. */
  const [errorEscritura, setErrorEscritura] = useState(null);
  const [estadoAlmacen, setEstadoAlmacen] = useState(null);

  const deps = useMemo(() => ({ guardar, leer }), []);

  /* Se comprueba el almacenamiento ANTES de que el usuario escriba nada.
     Descubrir que no se puede guardar después de redactar un reporte largo es
     la peor forma de enterarse. */
  useEffect(() => {
    let vivo = true;
    comprobarAlmacenamiento({ guardar, leer, borrar })
      .then((r) => vivo && setEstadoAlmacen(r))
      .catch(() => vivo && setEstadoAlmacen({ ok: false, motivo: "No se pudo comprobar el almacenamiento." }));
    return () => { vivo = false; };
  }, []);

  useEffect(() => {
    let vivo = true;
    leer(CLAVE)
      .then((g) => { if (vivo) setRegistros(Array.isArray(g) ? g : []); })
      .catch(() => { if (vivo) setRegistros([]); })
      .finally(() => { if (vivo) setCargando(false); });
    return () => { vivo = false; };
  }, []);

  const verificacion = useMemo(() => verificarConjunto(registros), [registros]);

  /* Huecos y repetidos en la numeración correlativa. Un faltante puede ser un
     formulario anulado, pero el auditor va a preguntar y hay que poder
     responder. */
  const numeracion = useMemo(() => huecosDeNumeracion(registros), [registros]);
  const kpis = useMemo(() => indicadores(registros), [registros]);
  const volumen = useMemo(() => saludDelVolumen(registros), [registros]);

  /* ═══════════════════════════════════════════════════════════════════════
     UN SOLO REGISTRO

     Había dos listas: esta y los cuatro depósitos del sistema anterior. Un
     auditor que ve dos listas no sabe cuál es la oficial, y el operador
     tampoco.

     Los depósitos viejos se leen para saber si queda algo por migrar. NO se
     borran: la migración copia, y vaciarlos es una decisión posterior del
     operador. ═══════════════════════════════════════════════════════════ */
  const [depositos, setDepositos] = useState(null);
  const [migrando, setMigrando] = useState(false);
  const [avisosMigracion, setAvisosMigracion] = useState(null);

  useEffect(() => {
    let vivo = true;
    Promise.all(DEPOSITOS.map(({ clave }) => leer(clave).catch(() => null)))
      .then((valores) => {
        if (!vivo) return;
        const mapa = {};
        DEPOSITOS.forEach(({ clave }, i) => { mapa[clave] = valores[i]; });
        setDepositos(mapa);
      })
      .catch(() => { if (vivo) setDepositos({}); });
    return () => { vivo = false; };
  }, []);

  const pendientes = useMemo(
    () => (depositos ? hayPendientes(registros, depositos) : 0),
    [registros, depositos],
  );

  /* ------------------------------------------------------------ acciones */

  const alta = useCallback(async (datos) => {
    /* Se arma con el FORMATO APROBADO del operador (FRM-SMS-002), no con
       campos inventados: un reporte capturado con campos distintos de los del
       formulario NO es el mismo registro, y un auditor que compare el papel
       con la pantalla tiene que ver lo mismo. */
    /* El riesgo evaluado viaja CON el registro. Antes se calculaba, se
       mostraba en pantalla y se perdía: un auditor que pregunte «¿cuál fue la
       evaluación de riesgo de este reporte?» no encontraba nada. */
    const { reporte, errores: erroresFormato, avisos } = armarReporte(datos, {
      numero: siguienteNumero(registros),
    });
    if (!reporte) return { ok: false, errores: erroresFormato, avisos };

    /* Y se envuelve en el registro con canal, bitácora y huella. El formulario
       aporta QUÉ se captura; el registro aporta el seguimiento. */
    const { registro, errores } = crearRegistro({
      tipo: "reporte",
      descripcion: reporte.descripcion,
      confidencial: Boolean(datos?.confidencial),
      reportante: reporte.nombre || datos?.reportante,
    });
    if (!registro) return { ok: false, errores, avisos };

    /* El formulario completo viaja adentro. Sin esto se perderían la fecha del
       evento, la aeronave, la zona y las recomendaciones — que son la mitad de
       lo que un auditor lee. */
    registro.formulario = reporte;
    registro.numero = reporte.numero;

    const r = await agregarConfirmado(CLAVE, registros, registro, deps);
    /* Solo se actualiza la pantalla si la escritura se confirmó. Mostrar el
       registro sin haberlo guardado es el error que este módulo corrige. */
    setRegistros(r.lista);
    setErrorEscritura(r.ok ? null : r.motivo);
    return { ok: r.ok, errores: r.ok ? [] : [r.motivo], avisos };
  }, [registros, deps]);

  const avanzar = useCallback(async (registro, hacia, motivo) => {
    /* El rol viaja con el nombre: «Juan Pérez» no dice si es el SMS Manager o
       el jefe de operaciones, y esa diferencia es la que un auditor mira. */
    const { registro: nuevo, errores } = cambiarEstado(registro, hacia, {
      quien: `${quien} (${ROLES_GESTOR.find(([c]) => c === rolGestor)?.[1] || rolGestor})`,
      motivo,
    });
    if (!nuevo) return { ok: false, errores };

    const r = await reemplazarConfirmado(CLAVE, registros, nuevo, deps);
    setRegistros(r.lista);
    setErrorEscritura(r.ok ? null : r.motivo);
    return { ok: r.ok, errores: r.ok ? [] : [r.motivo] };
  }, [registros, quien, deps]);

  const agregarNota = useCallback(async (registro, texto) => {
    const { registro: nuevo, errores } = anotar(registro, texto, { quien });
    if (!nuevo) return { ok: false, errores };

    const r = await reemplazarConfirmado(CLAVE, registros, nuevo, deps);
    setRegistros(r.lista);
    setErrorEscritura(r.ok ? null : r.motivo);
    return { ok: r.ok, errores: r.ok ? [] : [r.motivo] };
  }, [registros, quien, deps]);

  const anotarBarreras = useCallback(async (registro, barreras) => {
    const { registro: nuevo, errores, avisos } = registrarBarreras(registro, barreras, { quien });
    if (!nuevo) return { ok: false, errores, avisos };

    const r = await reemplazarConfirmado(CLAVE, registros, nuevo, deps);
    setRegistros(r.lista);
    setErrorEscritura(r.ok ? null : r.motivo);
    return { ok: r.ok, errores: r.ok ? [] : [r.motivo], avisos };
  }, [registros, quien, deps]);

  const migrarAhora = useCallback(async () => {
    if (!depositos) return;
    setMigrando(true);

    const r = migrar(registros, depositos);
    if (r.migrados === 0) {
      setMigrando(false);
      setAvisosMigracion({ resumen: r.resumen, avisos: r.avisos });
      return;
    }

    /* Se guarda con escritura confirmada, como todo. Si falla, la lista NO
       cambia: migrar a medias y creer que se migró entero sería peor que no
       migrar. */
    const guardado = await guardarConfirmado(CLAVE, r.lista, deps);
    setMigrando(false);

    if (!guardado.ok) {
      setErrorEscritura(`La migración NO se guardó: ${guardado.motivo}`);
      return;
    }

    setRegistros(r.lista);
    setErrorEscritura(null);
    setAvisosMigracion({ resumen: r.resumen, avisos: r.avisos });
  }, [registros, depositos, deps]);

  const exportarPaquete = useCallback(() => {
    const paquete = exportar(registros, {
      operador: PROCEDENCIA.operador,
      quienExporta: quien || null,
    });
    const texto = JSON.stringify(paquete, null, 2);
    const blob = new Blob([texto], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `sgs-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, [registros, quien]);

  /* --------------------------------------------------------------- vista */

  if (cargando) return <p className="sgs__cargando">Cargando registros…</p>;

  return (
    <section className="sgs">
      {/* El estado del almacenamiento, antes que nada. */}
      {estadoAlmacen && !estadoAlmacen.ok && (
        <div className="sgs__alerta sgs__alerta--grave" role="alert">
          <strong>No se pueden guardar registros</strong>
          <p>{estadoAlmacen.motivo}</p>
          <p>Hasta resolverlo, lo que escribas NO va a quedar guardado.</p>
        </div>
      )}

      {errorEscritura && (
        <div className="sgs__alerta sgs__alerta--grave" role="alert">
          <strong>El último registro NO se guardó</strong>
          <p>{errorEscritura}</p>
        </div>
      )}

      {(numeracion.huecos.length > 0 || numeracion.repetidos.length > 0) && (
        <div className="sgs__alerta sgs__alerta--precaucion" role="alert">
          <strong>Numeración con saltos</strong>
          {numeracion.huecos.length > 0 && (
            <p>Faltan los números: {numeracion.huecos.map((n) => numeroCorrelativo(n)).join(", ")}.
              Puede ser un formulario anulado; hay que poder explicarlo en la auditoría.</p>
          )}
          {numeracion.repetidos.length > 0 && (
            <p>Números repetidos: {numeracion.repetidos.map((n) => numeroCorrelativo(n)).join(", ")}.
              Dos reportes con el mismo número significa que uno pisó al otro.</p>
          )}
        </div>
      )}

      {!verificacion.intacto && registros.length > 0 && (
        <div className="sgs__alerta sgs__alerta--precaucion" role="alert">
          <strong>Integridad comprometida</strong>
          <p>{verificacion.resumen}</p>
          <p>Estos registros no se pueden presentar como evidencia sin explicar qué pasó.</p>
        </div>
      )}

      {/* Mientras haya dos listas, el operador tiene que saberlo. */}
      {pendientes > 0 && (
        <div className="sgs__alerta sgs__alerta--precaucion" role="alert">
          <strong>Hay dos registros separados</strong>
          <p>
            Quedan {pendientes} registros en el sistema anterior, fuera de esta lista.
            Un auditor que vea dos listas no sabe cuál es la oficial.
          </p>
          <p>
            La migración <strong>copia</strong>: los registros viejos no se borran, y lo
            migrado queda marcado con la fecha desde la que su integridad es verificable.
            Conviene exportar antes.
          </p>
          <button type="button" className="sgs__boton" onClick={migrarAhora} disabled={migrando}>
            {migrando ? "Migrando…" : `Unificar los ${pendientes} registros`}
          </button>
        </div>
      )}

      {avisosMigracion && (
        <div className="sgs__alerta sgs__alerta--precaucion" role="status">
          <strong>{avisosMigracion.resumen}</strong>
          {avisosMigracion.avisos.length > 0 && (
            <ul className="sgs__errores">
              {avisosMigracion.avisos.slice(0, 12).map((a) => <li key={a}>{a}</li>)}
              {avisosMigracion.avisos.length > 12 && (
                <li>…y {avisosMigracion.avisos.length - 12} avisos más.</li>
              )}
            </ul>
          )}
        </div>
      )}

      {volumen.mensaje && (
        <div className="sgs__alerta sgs__alerta--precaucion" role="status">
          <strong>{volumen.nivel === "urgente" ? "Archivar ya" : "Conviene archivar"}</strong>
          <p>{volumen.mensaje}</p>
        </div>
      )}

      {/* Quién actúa. Sin esto ningún registro puede avanzar — el dominio
          exige saber quién hace cada cambio, y antes venía del piloto
          asignado: sin vuelo cargado, el SGS quedaba congelado. */}
      <label className="sgs__campo">
        <span>Quién gestiona el SGS</span>
        <input
          value={quien}
          onChange={(e) => setQuien(e.target.value)}
          placeholder="Nombre del personal del SIG"
        />
      </label>
      <label className="sgs__campo">
        <span>Rol de quien gestiona</span>
        <select value={rolGestor} onChange={(e) => setRolGestor(e.target.value)}>
          {ROLES_GESTOR.map(([clave, nombre, nota]) => (
            <option key={clave} value={clave} title={nota}>{nombre}</option>
          ))}
        </select>
      </label>

      {!quien && (
        <p className="sgs__nota">
          Sin este dato no se pueden registrar cambios de estado ni anotaciones:
          cada movimiento tiene que decir quién lo hizo.
        </p>
      )}

      <Indicadores kpis={kpis} total={registros.length} intacto={verificacion.intacto} />

      <FormularioAlta onAlta={alta} quien={quien} />

      <ListaRegistros
        registros={registros}
        onAvanzar={avanzar}
        onAnotar={agregarNota}
        onBarreras={anotarBarreras}
        rolGestor={rolGestor}
      />

      <PreparacionBars />

      <div className="sgs__pie">
        <button type="button" className="sgs__boton" onClick={exportarPaquete}>
          Exportar para auditoría
        </button>
        <p className="sgs__procedencia">
          Matriz {PROCEDENCIA.matriz} · {PROCEDENCIA.manual} · Operador {PROCEDENCIA.operador}
        </p>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------- barreras */

/**
 * Análisis de barreras.
 *
 * Es lo que hace al SGS más útil que un registro de hechos: obliga a decir qué
 * DEFENSA falló, no solo qué pasó. «Se cargó combustible contaminado» no es
 * accionable; «la barrera de muestreo no se aplicó» dice qué arreglar.
 *
 * Estaba escrito en el dominio y NO se podía usar desde la pantalla: el módulo
 * existía y nadie lo llamaba.
 */
function EditorBarreras({ registro, onGuardar }) {
  const [barreras, setBarreras] = useState(() => registro.barreras || []);
  const [nombre, setNombre] = useState("");
  const [estado, setEstado] = useState("");
  const [mensajes, setMensajes] = useState({ errores: [], avisos: [] });

  /* Se sugieren las barreras que el operador ya tiene declaradas para las
     dimensiones de ESTE registro: empezar de una lista en blanco hace que cada
     uno invente un nombre distinto para la misma defensa, y después no se
     pueden contar. */
  const sugeridas = useMemo(() => {
    const dims = registro.dimensiones?.length ? registro.dimensiones : ["sms"];
    const vistas = new Set(barreras.map((b) => b.nombre));
    return [...new Set(dims.flatMap((d) => BARRERAS_TIPICAS[d] || []))]
      .filter((n) => !vistas.has(n));
  }, [registro.dimensiones, barreras]);

  const agregar = () => {
    if (!nombre.trim() || !estado) return;
    setBarreras((b) => [...b, { nombre: nombre.trim(), estado }]);
    setNombre("");
    setEstado("");
  };

  const guardar = async () => {
    const r = await onGuardar(registro, barreras);
    setMensajes({ errores: r.errores || [], avisos: r.avisos || [] });
  };

  const orientacion = orientacionDeBarreras(barreras);

  return (
    <div className="sgs__barreras">
      <span className="sgs__barreras-titulo">Barreras — ¿qué defensa falló?</span>

      {barreras.length > 0 && (
        <ul className="sgs__barreras-lista">
          {barreras.map((b, i) => (
            <li key={`${b.nombre}-${i}`} className={`sgs__barrera sgs__barrera--${b.estado}`}>
              <strong>{b.nombre}</strong>
              <span>{ESTADO_BARRERA.find(([c]) => c === b.estado)?.[1] || b.estado}</span>
              <button
                type="button"
                className="sgs__barrera-quitar"
                onClick={() => setBarreras((x) => x.filter((_, j) => j !== i))}
                aria-label={`Quitar ${b.nombre}`}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}

      {orientacion && <p className="sgs__barreras-orientacion">{orientacion}</p>}

      <div className="sgs__canal">
        <label className="sgs__campo">
          <span>Barrera</span>
          <input
            list={`barreras-${registro.id}`}
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            placeholder="Nombre de la defensa"
          />
          <datalist id={`barreras-${registro.id}`}>
            {sugeridas.map((n) => <option key={n} value={n} />)}
          </datalist>
        </label>

        <label className="sgs__campo">
          <span>Qué pasó con ella</span>
          <select value={estado} onChange={(e) => setEstado(e.target.value)}>
            <option value="">—</option>
            {ESTADO_BARRERA.map(([clave, etiqueta, ayuda]) => (
              <option key={clave} value={clave} title={ayuda}>{etiqueta}</option>
            ))}
          </select>
        </label>

        <button type="button" className="sgs__paso" onClick={agregar}>Agregar</button>
        <button type="button" className="sgs__boton" onClick={guardar}>Guardar análisis</button>
      </div>

      {mensajes.avisos.length > 0 && (
        <p className="sgs__nota">{mensajes.avisos.join(" ")}</p>
      )}
      {mensajes.errores.length > 0 && (
        <ul className="sgs__errores" role="alert">
          {mensajes.errores.map((e) => <li key={e}>{e}</li>)}
        </ul>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ BARS */

/**
 * Qué falta para una auditoría BARS.
 *
 * Va plegado: no es trabajo diario, es preparación. Pero tiene que estar a
 * mano — descubrir los huecos la semana de la auditoría es tarde.
 */
function PreparacionBars() {
  const resumen = useMemo(() => resumenPreparacion(), []);
  const pendientes = useMemo(() => listaDePreparacion(), []);

  return (
    <details className="sgs__bars">
      <summary>
        Preparación BARS — {resumen.completa} controles cubiertos,
        {" "}{resumen.huecos.length} con huecos
      </summary>

      <p className="sgs__nota">{resumen.advertencia}</p>

      <ul className="sgs__bars-lista">
        {pendientes.map((h) => (
          <li key={h.id} className={`sgs__bars-item sgs__bars-item--${h.grado}`}>
            <strong>{h.id} · {h.nombre}</strong>
            <span className="sgs__bars-grado">
              {h.grado === "ninguna" ? "Fuera del alcance de la aplicación" : "Evidencia parcial"}
            </span>
            <p>{h.falta}</p>
          </li>
        ))}
      </ul>
    </details>
  );
}

/* ------------------------------------------------------------ indicadores */

function Indicadores({ kpis, total, intacto }) {
  return (
    <div className="sgs__kpis">
      <Kpi rotulo="Registros" valor={total} />
      <Kpi rotulo="Abiertos" valor={kpis.abiertos} />
      <Kpi rotulo="Cerrados" valor={kpis.cerrados} />
      {/* Sin horas voladas la tasa es null: se muestra un guion, no un cero.
          Cero significaría «ningún evento por mil horas», que es una
          afirmación que no se puede hacer. */}
      <Kpi rotulo="Días a cierre" valor={kpis.diasPromedioCierre ?? "—"} />
      <Kpi rotulo="Integridad" valor={intacto ? "OK" : "REVISAR"} alerta={!intacto} />
    </div>
  );
}

function Kpi({ rotulo, valor, alerta }) {
  return (
    <div className={`sgs__kpi${alerta ? " es-alerta" : ""}`}>
      <span className="sgs__kpi-rotulo">{rotulo}</span>
      <strong className="sgs__kpi-valor">{valor}</strong>
    </div>
  );
}

/* -------------------------------------------------------------- formulario */

function FormularioAlta({ onAlta, quien }) {
  /* Los campos son los del FRM-SMS-002 Rev. 00, en el orden de sus cuatro
     secciones. Un auditor que compare el papel con la pantalla ve lo mismo. */
  const [tipoReporte, setTipoReporte] = useState("ocurrencia");
  const [sistema, setSistema] = useState("sms");
  const [fechaEvento, setFechaEvento] = useState("");
  const [zonaOperacion, setZonaOperacion] = useState("");
  const [aeronave, setAeronave] = useState("");
  const [rtv, setRtv] = useState("");
  const [via, setVia] = useState("aplicacion");
  const [descripcion, setDescripcion] = useState("");
  const [recomendaciones, setRecomendaciones] = useState("");
  const [confidencial, setConfidencial] = useState(false);
  const [rolReportante, setRolReportante] = useState("");
  /* En plural: un evento puede tocar varias dimensiones a la vez. */
  const [dimensiones, setDimensiones] = useState(["sms"]);
  const [probabilidad, setProbabilidad] = useState("");
  const [severidad, setSeveridad] = useState("");
  const [errores, setErrores] = useState([]);
  const [avisos, setAvisos] = useState([]);
  const [guardando, setGuardando] = useState(false);

  const riesgo = useMemo(
    () => (probabilidad && severidad ? evaluar(Number(probabilidad), severidad) : null),
    [probabilidad, severidad],
  );

  const enviar = async (e) => {
    e?.preventDefault?.();
    setGuardando(true);
    const r = await onAlta({
      tipoReporte, sistema, fechaEvento, zonaOperacion, aeronave, rtv, via,
      descripcion, recomendaciones, confidencial,
      nombre: confidencial ? "" : quien,
      reportante: quien,
      elaboradoPor: quien,
    });
    setGuardando(false);
    setErrores(r.errores || []);
    setAvisos(r.avisos || []);
    if (r.ok) {
      setDescripcion(""); setRecomendaciones("");
      setProbabilidad(""); setSeveridad("");
    }
  };

  return (
    <form className="sgs__alta" onSubmit={enviar}>
      <h3 className="sgs__titulo">
        {FORMATO.titulo} · {FORMATO.codigo} {FORMATO.revision}
      </h3>

      {/* El aviso del formulario, palabra por palabra. Es la garantía sobre la
          que se apoya todo el programa de reportes. */}
      <p className="sgs__no-punitivo">{AVISO_NO_PUNITIVO}</p>

      <div className="sgs__fila">
        <label className="sgs__campo">
          <span>Tipo de reporte</span>
          <select value={tipoReporte} onChange={(e) => setTipoReporte(e.target.value)}>
            {TIPOS_REPORTE.map(([c, etiqueta]) => (
              <option key={c} value={c}>{etiqueta}</option>
            ))}
          </select>
        </label>

        <label className="sgs__campo">
          <span>Sistema de gestión</span>
          <select value={sistema} onChange={(e) => setSistema(e.target.value)}>
            {SISTEMAS.map(([c, etiqueta]) => <option key={c} value={c}>{etiqueta}</option>)}
          </select>
        </label>
      </div>

      {/* Sección I */}
      <div className="sgs__fila">
        <label className="sgs__campo">
          <span>Fecha del evento</span>
          <input type="date" value={fechaEvento} onChange={(e) => setFechaEvento(e.target.value)} />
        </label>
        <label className="sgs__campo">
          <span>Zona de operación</span>
          <input value={zonaOperacion} onChange={(e) => setZonaOperacion(e.target.value)} />
        </label>
      </div>

      <div className="sgs__fila">
        <label className="sgs__campo">
          <span>Aeronave</span>
          <input value={aeronave} onChange={(e) => setAeronave(e.target.value)} />
        </label>
        <label className="sgs__campo">
          <span>RTV</span>
          <input value={rtv} onChange={(e) => setRtv(e.target.value)} />
        </label>
        <label className="sgs__campo">
          <span>Vía</span>
          <select value={via} onChange={(e) => setVia(e.target.value)}>
            {VIAS.map(([c, etiqueta]) => <option key={c} value={c}>{etiqueta}</option>)}
          </select>
        </label>
      </div>

      <label className="sgs__campo sgs__campo--confidencial">
        <input
          type="checkbox"
          checked={confidencial}
          onChange={(e) => setConfidencial(e.target.checked)}
        />
        <span>Confidencial</span>
      </label>

      {confidencial && (
        <p className="sgs__nota">
          Tu identidad queda separada del contenido y no aparece en listados,
          análisis ni exportaciones. Según el manual, solo personal del SIG
          puede levantarla.
        </p>
      )}

      {/* Sección III */}
      <label className="sgs__campo">
        <span>Descripción detallada</span>
        <textarea
          rows={5}
          value={descripcion}
          onChange={(e) => setDescripcion(e.target.value)}
          placeholder="Describe el evento de forma clara y precisa."
        />
      </label>

      {/* Las preguntas del formulario. Sin ellas, un reporte suele decir qué
          pasó y nada más — y sin los factores contribuyentes no se analiza. */}
      <ul className="sgs__guia">
        {GUIA_DESCRIPCION.map((g) => <li key={g}>{g}</li>)}
      </ul>

      {/* Sección IV */}
      <label className="sgs__campo">
        <span>Recomendaciones / sugerencias</span>
        <textarea
          rows={3}
          value={recomendaciones}
          onChange={(e) => setRecomendaciones(e.target.value)}
          placeholder="Cómo prevenir otro evento similar."
        />
      </label>

      <div className="sgs__fila">
        <label className="sgs__campo">
          <span>Probabilidad</span>
          <select value={probabilidad} onChange={(e) => setProbabilidad(e.target.value)}>
            <option value="">—</option>
            {PROBABILIDAD.map((p) => (
              <option key={p.valor} value={p.valor}>{p.valor} · {p.nombre}</option>
            ))}
          </select>
        </label>

        <label className="sgs__campo">
          <span>Severidad</span>
          <select value={severidad} onChange={(e) => setSeveridad(e.target.value)}>
            <option value="">—</option>
            {SEVERIDAD.map((sv) => (
              <option key={sv.valor} value={sv.valor}>{sv.valor} · {sv.nombre}</option>
            ))}
          </select>
        </label>
      </div>

      {riesgo && (
        <div className="sgs__riesgo" style={{ borderColor: riesgo.color }}>
          <strong style={{ color: riesgo.color }}>{riesgo.clave} · {riesgo.nivel}</strong>
          <p>{riesgo.accion}</p>
          {!riesgo.puedeOperar && (
            <p className="sgs__riesgo-alto">Este nivel requiere decisión formal antes de operar.</p>
          )}
        </div>
      )}

      {errores.length > 0 && (
        <ul className="sgs__errores" role="alert">
          {errores.map((e) => <li key={e}>{e}</li>)}
        </ul>
      )}

      {/* Los avisos NO son errores: un reporte sin nombre o sin aeronave es
          válido según el formulario. */}
      {avisos.length > 0 && (
        <ul className="sgs__avisos" role="status">
          {avisos.map((a) => <li key={a}>{a}</li>)}
        </ul>
      )}

      <button type="submit" className="sgs__boton" disabled={guardando}>
        {guardando ? "Guardando…" : "Registrar"}
      </button>
    </form>
  );
}

/* ----------------------------------------------------------------- lista */

/** Cuántos se muestran de una vez. */
const POR_PAGINA = 25;

function ListaRegistros({ registros, onAvanzar, onAnotar, onBarreras, rolGestor }) {
  const [soloAbiertos, setSoloAbiertos] = useState(false);
  const [cuantos, setCuantos] = useState(POR_PAGINA);

  const visibles = useMemo(() => {
    const orden = registros.slice().reverse();
    return soloAbiertos ? orden.filter((r) => !ESTADOS[r.estado]?.final) : orden;
  }, [registros, soloAbiertos]);

  if (registros.length === 0) {
    return <p className="sgs__vacio">Todavía no hay registros.</p>;
  }

  return (
    <div>
      <div className="sgs__filtros">
        <label className="sgs__campo--confidencial">
          <input
            type="checkbox"
            checked={soloAbiertos}
            onChange={(e) => { setSoloAbiertos(e.target.checked); setCuantos(POR_PAGINA); }}
          />
          <span>Solo los abiertos ({registros.filter((r) => !ESTADOS[r.estado]?.final).length})</span>
        </label>
      </div>

      {/* Se dibujan de a tandas: con miles de registros, pintarlos todos
          bloquea la pantalla justo cuando alguien quiere cargar un reporte. */}
      <ul className="sgs__lista">
        {visibles.slice(0, cuantos).map((r) => (
          <Fila key={r.id} registro={r} onAvanzar={onAvanzar} onAnotar={onAnotar} onBarreras={onBarreras} rolGestor={rolGestor} />
        ))}
      </ul>

      {visibles.length > cuantos && (
        <button
          type="button"
          className="sgs__paso"
          onClick={() => setCuantos((c) => c + POR_PAGINA)}
        >
          Ver más ({visibles.length - cuantos} restantes)
        </button>
      )}
    </div>
  );
}

function Fila({ registro, onAvanzar, onAnotar, onBarreras, rolGestor }) {
  /* Se muestra SIEMPRE la vista anónima: la identidad de un confidencial no
     aparece en ningún listado. */
  const v = vistaAnonima(registro);
  const estado = ESTADOS[v.estado];
  const [abierto, setAbierto] = useState(false);
  const [motivo, setMotivo] = useState("");
  const [nota, setNota] = useState("");
  const [errores, setErrores] = useState([]);

  const conflicto = conflictoDeInteres({
    rolGestor,
    dimensiones: v.dimensiones,
    rolReportante: v.rolReportante,
  });

  const paso = async (hacia) => {
    const r = await onAvanzar(registro, hacia, motivo);
    setErrores(r.errores || []);
    if (r.ok) setMotivo("");
  };

  const anotarAhora = async () => {
    const r = await onAnotar(registro, nota);
    setErrores(r.errores || []);
    if (r.ok) setNota("");
  };

  return (
    <li className="sgs__fila">
      <button type="button" className="sgs__fila-cabecera" onClick={() => setAbierto((a) => !a)}>
        <span className="sgs__fila-tipo">{ETIQUETA_TIPO[v.tipo] || v.tipo}</span>
        <span className="sgs__fila-estado">{estado?.etiqueta || v.estado}</span>
        <span className="sgs__fila-fecha">{v.creado?.slice(0, 10)}</span>
        {v.confidencial && <span className="sgs__fila-conf">confidencial</span>}
      </button>

      <p className="sgs__fila-desc">{v.descripcion}</p>

      {abierto && (
        <div className="sgs__detalle">
          <p className="sgs__meta">
            Reportado por {v.reportante || "—"} · huella {v.huella}
          </p>

          {v.anotaciones?.length > 0 && (
            <ul className="sgs__anotaciones">
              {v.anotaciones.map((a) => (
                <li key={`${a.cuando}-${a.texto.slice(0, 12)}`}>
                  <span>{a.cuando.slice(0, 16).replace("T", " ")} · {a.quien}</span>
                  <p>{a.texto}</p>
                </li>
              ))}
            </ul>
          )}

          {/* El canal: SOLO los pasos a los que este estado puede ir. Un menú
              con todos permitiría saltear la verificación de eficacia. */}
          <EditorBarreras registro={registro} onGuardar={onBarreras} />

          {/* El conflicto se ADVIERTE, no se impide: la aplicación no tiene
              control de acceso. Pero queda a la vista y en la bitácora, que es
              lo que un auditor busca. */}
          {conflicto.hay && (
            <p className="sgs__conflicto" role="alert">{conflicto.motivo}</p>
          )}

          {estado?.siguientes?.length > 0 && (
            <div className="sgs__canal">
              <label className="sgs__campo">
                <span>Motivo</span>
                <input value={motivo} onChange={(e) => setMotivo(e.target.value)} />
              </label>
              <div className="sgs__pasos">
                {estado.siguientes.map((s) => (
                  <button key={s} type="button" className="sgs__paso" onClick={() => paso(s)}>
                    {ESTADOS[s].etiqueta}
                  </button>
                ))}
              </div>
            </div>
          )}

          {estado?.final && (
            <p className="sgs__cerrado">
              Cerrado. Si aparece información nueva, se registra un hecho nuevo:
              un registro cerrado no se reabre.
            </p>
          )}

          <div className="sgs__canal">
            <label className="sgs__campo">
              <span>Anotación</span>
              <input value={nota} onChange={(e) => setNota(e.target.value)} />
            </label>
            <button type="button" className="sgs__paso" onClick={anotarAhora}>Anotar</button>
          </div>

          {errores.length > 0 && (
            <ul className="sgs__errores" role="alert">
              {errores.map((e) => <li key={e}>{e}</li>)}
            </ul>
          )}

          <details className="sgs__bitacora">
            <summary>Bitácora ({v.bitacora?.length || 0})</summary>
            <ol>
              {(v.bitacora || []).map((b, i) => (
                <li key={`${b.cuando}-${i}`}>
                  {b.cuando.slice(0, 16).replace("T", " ")} · {b.quien} · {b.accion}
                  {b.hacia ? ` → ${ESTADOS[b.hacia]?.etiqueta || b.hacia}` : ""}
                  {b.motivo ? ` · ${b.motivo}` : ""}
                </li>
              ))}
            </ol>
          </details>
        </div>
      )}
    </li>
  );
}
